import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Button, Form, Spinner, Alert } from 'react-bootstrap';
import {
  createPlan,
  fetchPlanById,
  updatePlan
} from '../../../services/salesPlan';
import { fetchSalesServices } from '../../../services/salesServiceService';

const AddEditPlan = () => {
  const navigate = useNavigate();
  const { id } = useParams(); // ID for editing

  // Define form state
  const [formData, setFormData] = useState<{
    service_id: string;
    plan_name: string;
    pricing: string;
    billing_period: string;
    plan_type: string;
    tier: string;
    max_users: string;
    status: string;
  }>({
    service_id: '',
    plan_name: '',
    pricing: '',
    billing_period: '',
    plan_type: '',
    tier: '',
    max_users: '',
    status: 'active' // Default status
  });

  const [services, setServices] = useState<
    { service_id: string; service_name: string }[]
  >([]);
  const [loading, setLoading] = useState(false); // Loading state for fetching data
  const [submitting, setSubmitting] = useState(false); // Loading state for form submission
  const [error, setError] = useState<string | null>(null); // Error handling

  // Fetch services for dropdown
  useEffect(() => {
    const loadServices = async () => {
      try {
        const servicesData = await fetchSalesServices();
        setServices(servicesData);
      } catch (error) {
        console.error('Error fetching services:', error);
      }
    };
    loadServices();
  }, []);

  // Fetch existing plan details if editing
  useEffect(() => {
    if (id) {
      setLoading(true);
      const loadPlan = async () => {
        try {
          const plan = await fetchPlanById(id);
          setFormData({
            service_id: plan.service_id.toString(),
            plan_name: plan.plan_name,
            pricing: plan.pricing.toString(),
            billing_period: plan.billing_period || '',
            plan_type: plan.plan_type || '',
            tier: plan.tier || '',
            max_users: plan.max_users ? plan.max_users.toString() : '',
            status: plan.CDC_FLAG === 'A' ? 'active' : 'inactive'
          });
        } catch (error) {
          setError('Failed to load plan details. Please try again.');
          console.error('Error fetching plan:', error);
        } finally {
          setLoading(false);
        }
      };
      loadPlan();
    }
  }, [id]);

  // Handle input change
  const handleChange = (e: React.ChangeEvent<HTMLElement>) => {
    const target = e.target as HTMLInputElement | HTMLSelectElement;
    setFormData(prevState => ({
      ...prevState,
      [target.name]: target.value
    }));
  };

  // Handle form submission (Create or Update)
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitting(true);
    setError(null);

    const planData = {
      ...formData,
      pricing: parseFloat(formData.pricing),
      max_users: formData.max_users ? parseInt(formData.max_users) : null,
      CDC_FLAG: formData.status === 'active' ? 'A' : 'I'
    };

    try {
      if (id) {
        await updatePlan(id, planData);
        alert('Plan updated successfully');
      } else {
        await createPlan(planData);
        alert('Plan added successfully');
      }
      navigate('/sales/plans/all-plans');
    } catch (error) {
      setError('Error submitting plan. Please try again.');
      console.error('Error submitting plan:', error);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="container">
      <h2>{id ? 'Edit' : 'Add'} Plan</h2>

      {error && <Alert variant="danger">{error}</Alert>}

      {loading ? (
        <div className="text-center">
          <Spinner animation="border" role="status">
            <span className="visually-hidden">Loading...</span>
          </Spinner>
          <p>Loading plan details...</p>
        </div>
      ) : (
        <Form onSubmit={handleSubmit}>
          {/* Service Selection */}
          <Form.Group>
            <Form.Label>Service</Form.Label>
            <Form.Select
              name="service_id"
              value={formData.service_id}
              onChange={handleChange}
              required
              disabled={submitting}
            >
              <option value="">Select Service</option>
              {services.map(service => (
                <option key={service.service_id} value={service.service_id}>
                  {service.service_name}
                </option>
              ))}
            </Form.Select>
          </Form.Group>

          {/* Plan Name */}
          <Form.Group>
            <Form.Label>Plan Name</Form.Label>
            <Form.Control
              type="text"
              name="plan_name"
              value={formData.plan_name}
              onChange={handleChange}
              required
              disabled={submitting}
            />
          </Form.Group>

          {/* Plan Pricing */}
          <Form.Group>
            <Form.Label>Pricing</Form.Label>
            <Form.Control
              type="number"
              name="pricing"
              value={formData.pricing}
              onChange={handleChange}
              required
              disabled={submitting}
            />
          </Form.Group>

          {/* Billing Period */}
          <Form.Group>
            <Form.Label>Billing Period</Form.Label>
            <Form.Select
              name="billing_period"
              value={formData.billing_period}
              onChange={handleChange}
              required
              disabled={submitting}
            >
              <option value="">Select Billing Period</option>
              <option value="Monthly">Monthly</option>
              <option value="Yearly">Yearly</option>
            </Form.Select>
          </Form.Group>

          {/* Plan Type */}
          <Form.Group>
            <Form.Label>Plan Type</Form.Label>
            <Form.Select
              name="plan_type"
              value={formData.plan_type}
              onChange={handleChange}
              required
              disabled={submitting}
            >
              <option value="">Select Plan Type</option>
              <option value="Per-user">Per-user</option>
              <option value="Per-team">Per-team</option>
              <option value="Enterprise">Enterprise</option>
            </Form.Select>
          </Form.Group>

          {/* Tier */}
          <Form.Group>
            <Form.Label>Tier</Form.Label>
            <Form.Select
              name="tier"
              value={formData.tier}
              onChange={handleChange}
              required
              disabled={submitting}
            >
              <option value="">Select Tier</option>
              <option value="Basic">Basic</option>
              <option value="Pro">Pro</option>
              <option value="Premium">Premium</option>
            </Form.Select>
          </Form.Group>

          {/* Max Users */}
          <Form.Group>
            <Form.Label>Max Users (optional)</Form.Label>
            <Form.Control
              type="number"
              name="max_users"
              value={formData.max_users}
              onChange={handleChange}
              disabled={submitting}
            />
          </Form.Group>

          {/* Submit Button */}
          <Button type="submit" className="mt-3" disabled={submitting}>
            {submitting ? (
              <>
                <Spinner animation="border" size="sm" /> Saving...
              </>
            ) : (
              `${id ? 'Update' : 'Add'} Plan`
            )}
          </Button>
        </Form>
      )}
    </div>
  );
};

export default AddEditPlan;
